import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";
import type {
	CompanyCreateRequest,
	CompanyUpdateRequest,
	CompanyUserRole,
	UpdateCompanyICPDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { QueryKeys } from "../LinkGen";
import { useSelectedCompany } from "./useSelectedCompany";

export function useCompanies() {
	return useSuspenseQuery({
		queryKey: QueryKeys.Company.All(),
		queryFn: async () => (await Api.GetCompanies()).data,
	}).data;
}

export const useCompany = () => {
	const companies = useCompanies();
	const [selected] = useSelectedCompany();

	return useMemo(() => {
		const company = companies.find((x) => x.id === selected) ?? companies[0];

		if (company) {
			return company;
		}

		throw new Error("Company not found");
	}, [companies, selected]);
};

export const useCompanyActions = () => {
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const inviteUser = useMutation({
		mutationFn: async ({
			companyId,
			email,
		}: {
			companyId: number;
			email: string;
		}) => {
			const data = (await Api.CompanyInviteUser(companyId, email)).data;
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});

			emit({
				type: AlertType.SUCCESS,
				content: "User has been invited to company.",
			});

			return data;
		},
	});

	const addCompanyUser = useMutation({
		mutationFn: async (props: {
			companyID: number;
			userEmail: string;
			role?: CompanyUserRole;
		}) => {
			await Api.CompanyInviteUser(props.companyID, props.userEmail, props.role);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
		},
	});

	const deleteCompanyUser = useMutation({
		mutationFn: async (props: { companyID: number; userID: number }) => {
			await Api.CompanyDeleteUser(props.companyID, props.userID);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
		},
	});

	const updateCompanyICP = useMutation({
		mutationFn: async (dto: UpdateCompanyICPDto) => {
			await Api.CompanyUpdateICP(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
		},
	});

	const updateCompany = useMutation({
		mutationFn: async (dto: CompanyUpdateRequest) => {
			await Api.UpdateCompanyInfo(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Company information saved.",
			});
		},
	});

	const createCompany = useMutation({
		mutationFn: async (dto: CompanyCreateRequest) => {
			await Api.CreateCompany(dto);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
		},
	});

	const deleteCompany = useMutation({
		mutationFn: async (id: number) => {
			await Api.DeleteCompany(id);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
		},
	});

	const renameCompany = useMutation({
		mutationFn: async ({ id, name }: { id: number; name: string }) => {
			await Api.RenameCompany(id, { name });
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Company name updated.",
			});
		},
	});

	const revokeInvite = useMutation({
		mutationFn: async (x: { companyID: number; inviteID: number }) => {
			await Api.CompanyRevokeUserInvite(x.companyID, x.inviteID);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
		},
	});

	const uploadLogo = useMutation({
		mutationFn: async (file: File) => {
			const { data } = await Api.UploadCompanyLogo(file);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Logo uploaded successfully.",
			});
			return data;
		},
	});

	const extractColorsFromLogo = useMutation({
		mutationFn: async () => {
			const { data } = await Api.ExtractColorsFromLogo();
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Brand colors extracted and saved.",
			});
			return data.colors;
		},
	});

	const updateBrandColors = useMutation({
		mutationFn: async (brand_colors: string[]) => {
			await Api.UpdateCompanyInfo({ brand_colors });
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Brand colors saved.",
			});
		},
	});

	const updateUserRole = useMutation({
		mutationFn: async ({
			companyID,
			userID,
			role,
		}: {
			companyID: number;
			userID: number;
			role: CompanyUserRole;
		}) => {
			await Api.UpdateUserRole(companyID, userID, role);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "User role updated.",
			});
		},
	});

	const updateApprovalSetting = useMutation({
		mutationFn: async (requirePostApproval: boolean) => {
			await Api.UpdateApprovalSetting(requirePostApproval);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Company.All(),
			});
			emit({
				type: AlertType.SUCCESS,
				content: requirePostApproval
					? "Post approval requirement enabled."
					: "Post approval requirement disabled.",
			});
		},
	});

	return {
		updateCompany,
		createCompany,
		deleteCompany,
		renameCompany,
		inviteUser,
		addCompanyUser,
		deleteCompanyUser,
		updateCompanyICP,
		revokeInvite,
		uploadLogo,
		extractColorsFromLogo,
		updateBrandColors,
		updateUserRole,
		updateApprovalSetting,
	};
};
